/**
 * effectConfig — the declarative source of truth for key -> effect mapping.
 *
 * Every effect behaves identically: momentary while its key is held, and
 * latched on (persistent) when the key is pressed with Shift. There is no
 * per-effect mode — the single input code path in InputController applies the
 * same on/off/latch rules to all 26 keys. Adding or rebalancing an effect means
 * editing this array, nothing else.
 */

export interface EffectDef {
  /** Normalized, layout-independent key identity (e.g. 'B', 'M', 'Semicolon'). */
  readonly key: string;
  /** Display glyph for the keycap when `key` isn't a plain letter (e.g. ';'). */
  readonly cap?: string;
  /** Stable effect id; also the paramBus param name once namespaced. */
  readonly effect: string;
  /** Human-facing label — the mnemonic the key hints at. */
  readonly mnemonic: string;
  /** One-line description for the on-screen legend. */
  readonly hint: string;
  /** What the +/- modifier tunes on this effect (shown above the −/+ keys). */
  readonly modLabel: string;
  /** Starting modifier value (0..1) if not the neutral 0.5 — e.g. Blackout /
   *  Whiteout start at full depth (1) so a bare press fully covers the frame. */
  readonly modDefault?: number;
  /** Whether `~`/auto-pilot random rolls may pick this effect. Default true;
   *  set false for full-frame effects (Blackout/Whiteout) that would just blank
   *  the screen if rolled at random. */
  readonly randomizable?: boolean;
}

// The 26 letters (the key letter hints at the effect) plus two punctuation keys
// (`;` Blackout, `'` Whiteout). Hold a key for a momentary hit, or Shift+key to
// latch it on. `hint` describes the look, not the gesture.
export const effectConfig: readonly EffectDef[] = [
  { key: 'A', effect: 'alien', mnemonic: 'Alien', hint: 'Psychedelic colour warp', modLabel: 'Spread' },
  { key: 'B', effect: 'bloom', mnemonic: 'Bloom', hint: 'Glow that swells on the beat', modLabel: 'Radius' },
  { key: 'C', effect: 'crystallize', mnemonic: 'Crystallize', hint: 'Shatter into stained-glass facets', modLabel: 'Facets' },
  { key: 'D', effect: 'distort', mnemonic: 'Distort', hint: 'Sine warp', modLabel: 'Amount' },
  { key: 'E', effect: 'explode', mnemonic: 'Explode', hint: 'Burst outward on every beat', modLabel: 'Force' },
  { key: 'F', effect: 'fracture', mnemonic: 'Fracture', hint: 'Shatter into shards', modLabel: 'Density' },
  { key: 'G', effect: 'glitch', mnemonic: 'Glitch', hint: 'Datamosh burst', modLabel: 'Intensity' },
  { key: 'H', effect: 'hue', mnemonic: 'Hue rotate', hint: 'Cycle the colour wheel', modLabel: 'Speed' },
  { key: 'I', effect: 'invert', mnemonic: 'Invert', hint: 'Flip all colours', modLabel: 'Amount' },
  { key: 'J', effect: 'jello', mnemonic: 'Jello', hint: 'Springy elastic wobble', modLabel: 'Frequency' },
  { key: 'K', effect: 'kaleido', mnemonic: 'Kaleido', hint: 'Extra mirror segments', modLabel: 'Segments' },
  { key: 'L', effect: 'lensflare', mnemonic: 'Lens Flare', hint: 'Roaming light with streaks and ghosts', modLabel: 'Rays' },
  { key: 'M', effect: 'melt', mnemonic: 'Melt', hint: 'Drip the pixels', modLabel: 'Drip' },
  { key: 'N', effect: 'noise', mnemonic: 'Noise', hint: 'Film-grain overlay', modLabel: 'Amount' },
  { key: 'O', effect: 'ocean', mnemonic: 'Ocean', hint: 'Underwater caustic light', modLabel: 'Scale' },
  { key: 'P', effect: 'pixellate', mnemonic: 'Pixellate', hint: 'Chunk into blocky pixels', modLabel: 'Block size' },
  { key: 'Q', effect: 'quake', mnemonic: 'Quake', hint: 'Screen shake', modLabel: 'Shake' },
  { key: 'R', effect: 'ripple', mnemonic: 'Ripple', hint: 'Concentric rings pulsing on the beat', modLabel: 'Frequency' },
  { key: 'S', effect: 'strobe', mnemonic: 'Strobe', hint: 'Beat-locked flash', modLabel: 'Depth', modDefault: 1 },
  { key: 'T', effect: 'twist', mnemonic: 'Twist', hint: 'Radial swirl', modLabel: 'Twist' },
  { key: 'U', effect: 'underwater', mnemonic: 'Underwater', hint: 'Wavy blur', modLabel: 'Waviness' },
  { key: 'V', effect: 'vignette', mnemonic: 'Vignette', hint: 'Darken the edges', modLabel: 'Spread' },
  { key: 'W', effect: 'warp', mnemonic: 'Warp', hint: 'Strong domain warp', modLabel: 'Amount' },
  { key: 'X', effect: 'xray', mnemonic: 'X-ray', hint: 'Edge / wireframe look', modLabel: 'Edges' },
  { key: 'Y', effect: 'yaw', mnemonic: 'Yaw', hint: 'Spin the whole frame', modLabel: 'Speed' },
  { key: 'Z', effect: 'zoom', mnemonic: 'Zoom', hint: 'Pulsing zoom', modLabel: 'Amount' },
  { key: 'Semicolon', cap: ';', effect: 'blackout', mnemonic: 'Blackout', hint: 'Fade the frame to black', modLabel: 'Depth', modDefault: 1, randomizable: false },
  { key: 'Quote', cap: '’', effect: 'whiteout', mnemonic: 'Whiteout', hint: 'Wash the frame to white', modLabel: 'Depth', modDefault: 1, randomizable: false },
] as const;

/** Fast key -> definition lookup for the input controller. */
export const effectByKey: ReadonlyMap<string, EffectDef> = new Map(
  effectConfig.map((def) => [def.key, def]),
);

/** paramBus key for an effect's on/off value (0 or 1). */
export function effectParam(effect: string): string {
  return `fx.${effect}`;
}

/**
 * paramBus key for an effect's latched flag (0/1), used only for UI feedback so
 * the dock can distinguish a latched (persistent) effect from a momentary one.
 * It lives in its own `latch.` namespace, separate from the `fx.*` effect values.
 */
export function latchParam(effect: string): string {
  return `latch.${effect}`;
}
